import {
  AnalysisGraphQL,
  AnalysisOutputStepType,
  GetAnalysisPipelineStep,
  isAnalysisOutputStepType,
  NonExecutableStepType,
  isNonExecutableStepType,
  isParameterOptimizationStepType,
  ParameterOptimizationStepType,
} from 'analysis-tool/AnalysisTypes';
import getDownstreamSteps from 'analysis-tool/data/getDownstreamSteps';
import getLegacyStepFromAnalysis from 'analysis-tool/data/getStepFromAnalysis';
import {STEP_TYPE_TO_USER_FACING_NAME} from 'graphql-client/__generated/GeneratedEnumMaps';
import {PipelineStepType, PipelineStepStatusCode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getChildSteps, getUpstreamSteps} from 'pipelines/StepLineageUtils';

import {isAnalysisToolBoxPlotEnabled} from './AnalysisToolFeatureGates';
import {isAnalysisOnlyLegacyChartStep} from './charting/AnalysisChartingUtils';
import {CanEditReturnValue, checkIsAnalysisArchived} from './EditAnalysisHelpers';

type NonRerunnableStepType = AnalysisOutputStepType | ParameterOptimizationStepType | NonExecutableStepType;

function isNonReRunnableStepType(stepType: PipelineStepType): stepType is NonRerunnableStepType {
  return (
    isAnalysisOutputStepType(stepType) ||
    isParameterOptimizationStepType(stepType) ||
    isNonExecutableStepType(stepType)
  );
}

export const NON_RERUNNABLE_STEP_TYPE_TO_USER_ERROR_MESSAGE: {
  [key in NonRerunnableStepType]: string;
} = {
  [PipelineStepType.TEMPORARY_CREATE_ANTIBODIES_AND_COMPONENTS]: 'used to create antibodies and components',
  [PipelineStepType.BULK_IMPORT]: 'used to create Benchling objects',
  [PipelineStepType.EXPLORATORY_DATA_ANALYSIS]: 'used in Paramter Optimization',
  [PipelineStepType.EXPORT_DATA_CATALOG_DATASET]: 'used to create datasets',
  [PipelineStepType.EXPORT_DATA_CATALOG_FILE]: 'used to create files',
  [PipelineStepType.NOTEBOOK_TABLE_DATASET]: 'created from a Notebook table',
  [PipelineStepType.PARAMETER_OPTIMIZATION_CONFIGURATION]: 'used in Parameter Optimization',
  [PipelineStepType.PARAMETER_OPTIMIZATION_DATASET_CONFIGURATION]: 'used in Parameter Optimization',
  [PipelineStepType.PARAMETER_OPTIMIZATION_MODELING]: 'used in Parameter Optimization',
  [PipelineStepType.PARAMETER_OPTIMIZATION_PREDICTION]: 'used to create predictions',
  [PipelineStepType.SAVE_TO_RESULTS]: 'used to create results',
  [PipelineStepType.SEND_TO_DOCUMENT]: 'used to send data',
  [PipelineStepType.TEMPLATE_INPUT]: 'used as a static input table from a template',
  [PipelineStepType.THIRD_PARTY]: 'to export data',
};

function checkPermissions(analysis: AnalysisGraphQL): CanEditReturnValue | null {
  // This message should not actually be shown to the user, because options to modify the analysis
  // should be hidden if the analysis is read-only. This check is included for completeness.
  if (!analysis.permissions.writable) {
    return {
      canEdit: false,
      message: 'You do not have permissions to modify this analysis.',
    };
  }
  return null;
}

function checkHasDownstreamFlagRows(
  analysis: AnalysisGraphQL,
  stepTypes: Set<PipelineStepType>
): CanEditReturnValue | null {
  if (stepTypes.has(PipelineStepType.FLAG_ROWS)) {
    return {
      canEdit: false,
      message: 'This table cannot be replaced because it has flagged rows.',
    };
  }
  return null;
}

function checkHasNonRerunnableStep(
  baseMessage: string,
  stepTypes: Set<PipelineStepType>
): CanEditReturnValue | null {
  for (const stepType of stepTypes) {
    if (isNonReRunnableStepType(stepType)) {
      return {
        canEdit: false,
        message: `${baseMessage} it is ${NON_RERUNNABLE_STEP_TYPE_TO_USER_ERROR_MESSAGE[stepType]}.`,
      };
    }
  }
  return null;
}

/**
 * Check if a step is an output step type (creates data outside the analysis).
 * These steps can never be modified or deleted.
 */
function checkIsOutputStepType(
  baseMessage: string,
  stepTypes: Set<PipelineStepType>
): CanEditReturnValue | null {
  for (const stepType of stepTypes) {
    if (isAnalysisOutputStepType(stepType)) {
      return {
        canEdit: false,
        message: `${baseMessage} it is ${NON_RERUNNABLE_STEP_TYPE_TO_USER_ERROR_MESSAGE[stepType]}.`,
      };
    }
  }
  return null;
}

export function checkIfAnalysisOnlyChartingType(
  baseMessage: string,
  downstreamSteps: Array<GetAnalysisPipelineStep>
): CanEditReturnValue | null {
  if (
    downstreamSteps.some((step) => isAnalysisOnlyLegacyChartStep(step) && !isAnalysisToolBoxPlotEnabled())
  ) {
    return {
      canEdit: false,
      message: `${baseMessage} the Insights Lab Beta is not enabled.`,
    };
  }
  return null;
}

function checkNotRegressionOrRegressionNotUsed(
  baseMessage: string,
  analysis: AnalysisGraphQL,
  step: GetAnalysisPipelineStep
): CanEditReturnValue | null {
  const regressionSteps = [step, ...getDownstreamSteps(analysis, step.id)].filter(
    (downstreamStep) => downstreamStep.definition.stepType === PipelineStepType.REGRESSION
  );

  for (const regressionStep of regressionSteps) {
    if (
      [...getDownstreamSteps(analysis, regressionStep.id)].some(
        (downstreamStep) => downstreamStep.definition.stepType === PipelineStepType.REGRESSION_INTERPOLATION
      )
    ) {
      return {
        canEdit: false,
        message: `${baseMessage} it is used to create a regression that is used elsewhere in this analysis.`,
      };
    }
  }

  return null;
}

function checkHasRunningStep(
  steps: Array<GetAnalysisPipelineStep>,
  baseMessage: string
): CanEditReturnValue | null {
  const hasRunningStep = steps.find((step) => step.statusCode === PipelineStepStatusCode.RUNNING);
  if (hasRunningStep) {
    return {
      canEdit: false,
      message: `${baseMessage} the analysis is still running.`,
    };
  }
  return null;
}

function checkIsStepTypeUnsupported(
  step: GetAnalysisPipelineStep,
  unsupportedStepTypes: Set<PipelineStepType>,
  baseMessage: string
): CanEditReturnValue | null {
  if (unsupportedStepTypes.has(step.definition.stepType)) {
    return {
      canEdit: false,
      message: `${baseMessage} is not supported for ${STEP_TYPE_TO_USER_FACING_NAME[
        step.definition.stepType
      ].toLowerCase()} steps.`,
    };
  }
  return null;
}

function checkIsUsedToCreateNewTable(
  stepTypes: Set<PipelineStepType>,
  baseMessage: string
): CanEditReturnValue | null {
  if (stepTypes.has(PipelineStepType.PROMOTE_DATASET)) {
    return {
      canEdit: false,
      message: `${baseMessage} it is used to create a new table.`,
    };
  }

  return null;
}

function checkIsUsedInJoin(stepTypes: Set<PipelineStepType>, baseMessage: string): CanEditReturnValue | null {
  if (stepTypes.has(PipelineStepType.JOIN)) {
    return {
      canEdit: false,
      message: `${baseMessage} it is used in a join.`,
    };
  }

  return null;
}

function checkIsUsedInUnion(
  stepTypes: Set<PipelineStepType>,
  baseMessage: string
): CanEditReturnValue | null {
  if (stepTypes.has(PipelineStepType.UNION_DATASETS)) {
    return {
      canEdit: false,
      message: `${baseMessage} it is used in a union.`,
    };
  }

  return null;
}

function checkOutputsSingleDataFrame(
  step: GetAnalysisPipelineStep,
  baseMessage: string
): CanEditReturnValue | null {
  if (step.outputStepDatas.length !== 1 || step.outputStepDatas[0].dataFrames.length !== 1) {
    return {
      canEdit: false,
      message: `${baseMessage} its source is not a table.`,
    };
  }
  return null;
}

/**
 * Check if a step can be modified (or removed) based on generic criteria:
 *
 * • If the analysis is archived, the step cannot be modified
 * • If a step is upstream from SAVE_TO_RESULTS, or other step that creates data outside the Analysis, the step cannot be modified
 * • If a step is upstream from a step type that does not support re-running, the step cannot be modified
 * • If a step is upstream from a step that creates a new top-level table–JOIN, UNION, or PROMOTE–the step cannot be removed
 *
 * TODO(BNCH-125860): Add flag rows logic here
 *
 * @param analysis The analysis
 * @param stepId The step to modify (or remove)
 * @param userFacingTerm The user-facing term of what is being modified (or removed)
 * @param toRemove True if the step is to be removed; false if it is to be modified
 * @param checkAnalysisProperties Whether properties on the analysis object should be checked. Usually should
 * be left to true, but you may want to set this to false if these properties have already been checked and
 * you don't want to repeat a message.
 * @returns {canEdit} True if the step is safe to modify (or remove) based on general criteria
 * @returns {message} A user-facing message explaining why the step cannot be modified (or removed)
 */
export function checkCanModifyStep(
  analysis: AnalysisGraphQL,
  stepId: string,
  userFacingTerm: 'view' | 'table' | 'step',
  toRemove: boolean,
  checkAnalysisProperties: boolean = true
): CanEditReturnValue {
  const step = getLegacyStepFromAnalysis(analysis, stepId);
  const downstreamSteps = [...getDownstreamSteps(analysis, stepId)];
  const stepTypes = new Set<PipelineStepType>(
    [...downstreamSteps.values()].map((step) => step.definition.stepType)
  );

  const baseMessage = `This ${userFacingTerm} cannot be ${toRemove ? 'removed' : 'modified'} because`;

  const checks = [
    ...(checkAnalysisProperties
      ? [() => checkIsAnalysisArchived(analysis), () => checkPermissions(analysis)]
      : []),
    // Always check downstream steps for non-rerunnable types
    () => checkHasNonRerunnableStep(baseMessage, stepTypes),
  ];

  // For the step itself:
  // - Output step types (SAVE_TO_RESULTS, etc.) can never be modified or deleted
  // - Non-executable types (NOTEBOOK_TABLE_DATASET, etc.) can be deleted but not modified
  // - Parameter optimization types can be deleted but not modified
  if (toRemove) {
    checks.push(() => checkIsOutputStepType(baseMessage, new Set([step.definition.stepType])));
  } else {
    checks.push(() => checkHasNonRerunnableStep(baseMessage, new Set([step.definition.stepType])));
  }

  if (!toRemove) {
    checks.push(() => checkIfAnalysisOnlyChartingType(baseMessage, [step, ...downstreamSteps]));
  }
  if (toRemove) {
    checks.push(() => checkIsUsedInUnion(stepTypes, baseMessage));
    checks.push(() => checkIsUsedInJoin(stepTypes, baseMessage));
    checks.push(() => checkIsUsedToCreateNewTable(stepTypes, baseMessage));
    checks.push(() => checkNotRegressionOrRegressionNotUsed(baseMessage, analysis, step));
  }
  for (const check of checks) {
    const result = check();
    if (result != null) {
      return result;
    }
  }

  return {canEdit: true, message: null};
}

/**
 * Check if a new step can be inserted after an existing step based on generic criteria.
 *
 * This is similar to `checkCanModifyStep`, but if `outputDataFrameId` is passed will only consider steps
 * downstream from a particular output data frame of a (multi-output) step.
 *
 * @param analysis The analysis
 * @param stepId The step to modify (or remove)
 * @param userFacingTerm The user-facing term of what is being modified (or removed)
 * @param outputDataFrameId If passed, only considers steps downstream from a particular output data from of the (multi-output) step
 * @returns {canEdit} True if it is safe to insert a new step after the current step based on general criteria
 * @returns {message} A user-facing message explaining why a step cannot be added
 */
export function checkCanInsertStep(
  analysis: AnalysisGraphQL,
  stepId: string,
  userFacingTerm: 'view' | 'table',
  outputDataFrameId?: string
): CanEditReturnValue {
  if (outputDataFrameId == null) {
    return checkCanModifyStep(analysis, stepId, userFacingTerm, false);
  }

  // Find only the child steps that use the output data frame
  const childSteps = getChildSteps(analysis, getLegacyStepFromAnalysis(analysis, stepId)).filter(
    (childStep) =>
      childStep.inputStepDatas.some((stepData) =>
        stepData.dataFrames.some(({id}) => id === outputDataFrameId)
      )
  );

  for (const childStep of childSteps) {
    const result = checkCanModifyStep(analysis, childStep.id, userFacingTerm, false);
    if (!result.canEdit) {
      return result;
    }
  }

  return {canEdit: true, message: null};
}

/**
 * Check if a step can be replaced based on generic criteria:
 *
 * • If the analysis is archived, the step cannot be replaced
 * • If a step is upstream from SAVE_TO_RESULTS, the step cannot be replaced
 * • If a step is a PROCEDURE_RUN_DATA_FRAMES or THIRD_PARTY step, it cannot be replaced (due to multi output steps, we will encourage them to use templates in the future.)
 *
 * @param analysis The analysis
 * @param step The step to modify (or remove)
 * @returns {canEdit} True if the step is safe to replace based on general criteria
 * @returns {message} A user-facing message explaining why the step cannot be modified (or removed)
 */
export function checkCanReplaceStep(
  analysis: AnalysisGraphQL,
  step: GetAnalysisPipelineStep
): CanEditReturnValue {
  const downstreamSteps = [...getDownstreamSteps(analysis, step.id)];
  const upstreamSteps = getUpstreamSteps(analysis, step);
  const stepTypes = new Set<PipelineStepType>(
    [...downstreamSteps.values()].map((step) => step.definition.stepType)
  );
  const allSteps = downstreamSteps.concat(upstreamSteps, [step]);

  const checks = [
    () => checkIsAnalysisArchived(analysis),
    () => checkPermissions(analysis),
    () => checkHasDownstreamFlagRows(analysis, stepTypes),
    () => checkHasNonRerunnableStep('This table cannot be replaced because', stepTypes),
    () =>
      checkIsStepTypeUnsupported(
        step,
        new Set([PipelineStepType.PROCEDURE_RUN_DATA_FRAMES, PipelineStepType.THIRD_PARTY]),
        'Replace table'
      ),
    () => checkHasRunningStep(allSteps, 'This table cannot be replaced because'),
    () => checkOutputsSingleDataFrame(step, 'This table cannot be replaced because'),
  ];

  for (const check of checks) {
    const result = check();
    if (result != null) {
      return result;
    }
  }

  return {canEdit: true, message: null};
}
